import { IonButton, IonCardContent, IonCardHeader, IonIcon, IonRow } from '@ionic/react';
import classNames from 'classnames';
import { informationCircle, resize } from 'ionicons/icons';
import React, { FC } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { selectIsMetric } from 'src/redux/profile-preferences/selectors';
import { JobLogV2Data } from 'src/utils/logs/types';
import { JobMessages } from '../../intl/JobMessages';
import { DqText } from '../dq-text';
import { WaterPressureModal } from './information/information-modals/WaterPressureModal';
import { JobWaterPressureGraphTranslator } from './util/JobWaterPressureGraphTranslator';
import { JobPressureGraphDataSegment } from './util/types';
import WaterPressureGraph, { WaterPressureGraphRef } from './water-pressure/WaterPressureGraph';

interface JobWaterPressureGraphProps {
  /** Water pressure data */
  waterData: JobLogV2Data['water'] | undefined;
  /** The time table used to correlate events with their calendary time. */
  timeTable: JobLogV2Data['timeTable'] | undefined;
  /** Time range min value */
  timeRangeMin: number;
  /** Time range max value */
  timeRangeMax: number;
  /** The timezone associated with the job log. */
  timezone: string;
  showTimestamps: boolean;
}

const JobWaterPressureGraph: FC<JobWaterPressureGraphProps> = ({
  waterData,
  timeTable,
  timeRangeMin,
  timeRangeMax,
  showTimestamps,
  timezone,
}) => {
  const intl = useIntl();
  const [helpDialogOpen, setHelpDialogOpen] = React.useState<boolean>(false);
  const isMetric = useSelector(selectIsMetric);
  const graphRef = React.useRef<WaterPressureGraphRef | null>(null);
  const [isZoomed, setZoomed] = React.useState(false);

  /** The view model data for the pressure graph. */
  const graphData = React.useMemo<JobPressureGraphDataSegment>(() => {
    if (!waterData || !timeTable) return [];
    return JobWaterPressureGraphTranslator.parseDataV2(waterData, timeTable, timeRangeMin, timeRangeMax, isMetric);
  }, [waterData, timeTable, timeRangeMin, timeRangeMax, isMetric]);

  return (
    <>
      <IonCardHeader>
        <IonRow className="ion-justify-content-between ion-align-items-center">
          <DqText variant="h3">{intl.formatMessage(JobMessages.waterPressureLabel)}</DqText>
          <div>
            {isZoomed && (
              <IonButton fill="clear" color="dark" onClick={graphRef.current?.resetZoom}>
                <IonIcon slot="icon-only" icon={resize} />
              </IonButton>
            )}
            <IonButton fill="clear" color="primary" onClick={() => setHelpDialogOpen(true)}>
              <IonIcon slot="icon-only" icon={informationCircle} />
            </IonButton>
          </div>
        </IonRow>
      </IonCardHeader>
      <IonCardContent>
        {!!graphData && graphData.length ? (
          <div className={classNames(!showTimestamps && 'graph-timestamp-no-print')}>
            <WaterPressureGraph
              data={graphData}
              hideTimestamps={!showTimestamps}
              ref={graphRef}
              onZoom={setZoomed}
              timezone={timezone}
            />
          </div>
        ) : (
          <IonRow className="ion-justify-content-center">
            <FormattedMessage {...JobMessages.waterPressureDataEmpty} />
          </IonRow>
        )}
      </IonCardContent>
      <WaterPressureModal isOpen={helpDialogOpen} onClose={() => setHelpDialogOpen(false)} />
    </>
  );
};

export default JobWaterPressureGraph;
